import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest } from '../../lib/queryClient';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { Client, ClientInput } from '../../hooks/use-clients';
import { useToast } from '../../hooks/use-toast';
import { emailSchema, phoneSchema } from '../../lib/validation';

// Schema de validación
const clientFormSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  email: emailSchema,
  phone: phoneSchema,
  address: z.string().optional().or(z.literal("")),
  city: z.string().optional().or(z.literal("")),
  state: z.string().optional().or(z.literal("")),
  zip: z.string().optional().or(z.literal("")),
  notes: z.string().optional().or(z.literal("")),
});

type ClientFormProps = {
  client?: Client;
  onSubmit: (data: ClientInput) => void;
  isSubmitting: boolean;
  onCancel: () => void;
};

export default function ClientForm({ client, onSubmit, isSubmitting, onCancel }: ClientFormProps) {
  const { toast } = useToast();
  const [uniquenessStatus, setUniquenessStatus] = useState<{
    email?: { isUnique: boolean; message: string; existingClient?: any };
    phone?: { isUnique: boolean; message: string; existingClient?: any };
    address?: { isUnique: boolean; message: string; existingClient?: any };
  }>({});
  const [isCheckingUniqueness, setIsCheckingUniqueness] = useState(false);

  // Formulario con valores por defecto
  const form = useForm<z.infer<typeof clientFormSchema>>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: {
      firstName: client?.firstName || "",
      lastName: client?.lastName || "",
      email: client?.email || "",
      phone: client?.phone || "",
      address: client?.address || "",
      city: client?.city || "",
      state: client?.state || "",
      zip: client?.zip || "",
      notes: client?.notes || "",
    },
  });

  // Check uniqueness when email, phone, or address changes
  const checkUniqueness = async (field: string, value: string) => {
    if (!value || value.trim() === '') {
      setUniquenessStatus(prev => ({ ...prev, [field]: undefined }));
      return;
    }

    // For phone, validate format first (must be 10 digits)
    if (field === 'phone') {
      const digits = value.replace(/\D/g, '');
      if (digits.length !== 10) {
        setUniquenessStatus(prev => ({
          ...prev,
          [field]: {
            isUnique: false,
            message: 'Phone number must be 10 digits (e.g., (123) 456-7890)'
          }
        }));
        return;
      }
    }

    // For email, validate format first
    if (field === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setUniquenessStatus(prev => ({
          ...prev,
          [field]: {
            isUnique: false,
            message: 'Please enter a valid email address'
          }
        }));
        return;
      }
    }

    if (value.length < 3) {
      setUniquenessStatus(prev => ({ ...prev, [field]: undefined }));
      return;
    }

    setIsCheckingUniqueness(true);
    try {
      const response = await apiRequest('POST', '/api/protected/clients/check-uniqueness', { [field]: value });
      const result = await response.json();
      
      if (response.ok) {
        setUniquenessStatus(prev => ({
          ...prev,
          [field]: { isUnique: true, message: `${field.charAt(0).toUpperCase() + field.slice(1)} is available` }
        }));
      } else if (response.status === 409) {
        setUniquenessStatus(prev => ({
          ...prev,
          [field]: { 
            isUnique: false, 
            message: `Client already exists with this ${field}`,
            existingClient: result.existingClient
          }
        }));
      }
    } catch (error) {
      console.error('Error checking uniqueness:', error);
    } finally {
      setIsCheckingUniqueness(false);
    }
  };

  // Debounced uniqueness check (only if format is valid)
  useEffect(() => {
    const email = form.watch('email');
    const phone = form.watch('phone');
    const address = form.watch('address');

    const timeoutId = setTimeout(() => {
      // Validate email format before checking uniqueness
      if (email && email.trim() !== '') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(email)) {
          checkUniqueness('email', email);
        } else {
          // Invalid email format - show error
          setUniquenessStatus(prev => ({
            ...prev,
            email: {
              isUnique: false,
              message: 'Please enter a valid email address'
            }
          }));
        }
      } else {
        setUniquenessStatus(prev => ({ ...prev, email: undefined }));
      }

      // Validate phone format before checking uniqueness (must be 10 digits)
      if (phone && phone.trim() !== '') {
        const phoneDigits = phone.replace(/\D/g, '');
        if (phoneDigits.length === 10) {
          checkUniqueness('phone', phone);
        } else {
          // Invalid phone format - show error
          setUniquenessStatus(prev => ({
            ...prev,
            phone: {
              isUnique: false,
              message: `Phone number must be 10 digits (currently ${phoneDigits.length} digits). Please enter a valid US phone number (e.g., (123) 456-7890)`
            }
          }));
        }
      } else {
        setUniquenessStatus(prev => ({ ...prev, phone: undefined }));
      }

      // Address doesn't need format validation
      if (address && address.trim() !== '') {
        checkUniqueness('address', address);
      } else {
        setUniquenessStatus(prev => ({ ...prev, address: undefined }));
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [form.watch('email'), form.watch('phone'), form.watch('address')]);

  const handleSubmit = (data: z.infer<typeof clientFormSchema>) => {
    // Validate phone format if provided
    if (data.phone && data.phone.trim() !== '') {
      const phoneDigits = data.phone.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        toast({
          title: "Invalid Phone Number",
          description: "Please enter a valid US phone number with 10 digits (e.g., (123) 456-7890)",
          variant: "destructive"
        });
        form.setError('phone', {
          type: 'manual',
          message: 'Phone number must be 10 digits'
        });
        return;
      }
    }

    // Validate email format if provided
    if (data.email && data.email.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.email)) {
        toast({
          title: "Invalid Email Address",
          description: "Please enter a valid email address (e.g., name@example.com)",
          variant: "destructive"
        });
        form.setError('email', {
          type: 'manual',
          message: 'Please enter a valid email address'
        });
        return;
      }
    }

    // Check if there are any uniqueness conflicts
    const hasConflicts = Object.values(uniquenessStatus).some(
      status => status && status.isUnique === false
    );

    if (hasConflicts) {
      toast({
        title: "Duplicate Client Detected",
        description: "A client with this information already exists. Please review the details.",
        variant: "destructive"
      });
      return;
    }

    onSubmit(data);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="remodra-card p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-slate-300 font-medium">First Name</FormLabel>
                <FormControl>
                  <Input placeholder="First Name" {...field} className="remodra-input" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-slate-300 font-medium">Last Name</FormLabel>
                <FormControl>
                  <Input placeholder="Last Name" {...field} className="remodra-input" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-slate-300 font-medium">Email</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input 
                      type="email" 
                      placeholder="email@example.com" 
                      {...field}
                      className={`remodra-input ${uniquenessStatus.email ? 
                        (uniquenessStatus.email.isUnique ? 'border-green-500' : 'border-red-500') : ''
                      }`}
                    />
                    {isCheckingUniqueness && field.value && (
                      <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-gray-400" />
                    )}
                    {uniquenessStatus.email && !isCheckingUniqueness && (
                      <div className="absolute right-3 top-3">
                        {uniquenessStatus.email.isUnique ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                    )}
                  </div>
                </FormControl>
                <FormMessage />
                {form.formState.errors.email && (
                  <p className="text-sm text-red-600">
                    {form.formState.errors.email.message}
                  </p>
                )}
                {uniquenessStatus.email && !form.formState.errors.email && (
                  <p className={`text-sm ${uniquenessStatus.email.isUnique ? 'text-green-600' : 'text-red-600'}`}>
                    {uniquenessStatus.email.message}
                  </p>
                )}
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-slate-300 font-medium">Phone</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input 
                      placeholder="(123) 456-7890" 
                      {...field}
                      className={`remodra-input ${
                        field.value && field.value.trim() !== '' 
                          ? (uniquenessStatus.phone?.isUnique === true 
                              ? 'border-green-500' 
                              : (uniquenessStatus.phone?.isUnique === false || form.formState.errors.phone
                                  ? 'border-red-500' 
                                  : ''))
                          : ''
                      }`}
                    />
                    {isCheckingUniqueness && field.value && (
                      <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-gray-400" />
                    )}
                    {uniquenessStatus.phone && !isCheckingUniqueness && field.value && field.value.trim() !== '' && (
                      <div className="absolute right-3 top-3">
                        {uniquenessStatus.phone.isUnique === true ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                    )}
                    {!uniquenessStatus.phone && field.value && field.value.trim() !== '' && !isCheckingUniqueness && form.formState.errors.phone && (
                      <div className="absolute right-3 top-3">
                        <AlertCircle className="h-4 w-4 text-red-500" />
                      </div>
                    )}
                  </div>
                </FormControl>
                <FormMessage />
                {form.formState.errors.phone && (
                  <p className="text-sm text-red-600">
                    {form.formState.errors.phone.message}
                  </p>
                )}
                {uniquenessStatus.phone && !form.formState.errors.phone && (
                  <p className={`text-sm ${uniquenessStatus.phone.isUnique ? 'text-green-600' : 'text-red-600'}`}>
                    {uniquenessStatus.phone.message}
                  </p>
                )}
              </FormItem>
            )}
          />
        </div>
        
        <FormField
          control={form.control}
          name="address"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-slate-300 font-medium">Address</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input 
                    placeholder="Address" 
                    {...field}
                    className={`remodra-input ${uniquenessStatus.address ? 
                      (uniquenessStatus.address.isUnique ? 'border-green-500' : 'border-red-500') : ''
                    }`}
                  />
                  {isCheckingUniqueness && field.value && (
                    <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-gray-400" />
                  )}
                  {uniquenessStatus.address && !isCheckingUniqueness && (
                    <div className="absolute right-3 top-3">
                      {uniquenessStatus.address.isUnique ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                  )}
                </div>
              </FormControl>
              <FormMessage />
              {uniquenessStatus.address && (
                <p className={`text-sm ${uniquenessStatus.address.isUnique ? 'text-green-600' : 'text-red-600'}`}>
                  {uniquenessStatus.address.message}
                </p>
              )}
            </FormItem>
          )}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-slate-300 font-medium">City</FormLabel>
                <FormControl>
                  <Input placeholder="City" {...field} className="remodra-input" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="state"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-slate-300 font-medium">State</FormLabel>
                <FormControl>
                  <Input placeholder="State" {...field} className="remodra-input" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="zip"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-slate-300 font-medium">ZIP Code</FormLabel>
                <FormControl>
                  <Input placeholder="ZIP Code" {...field} className="remodra-input" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-slate-300 font-medium">Notes</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Additional notes about this client..." 
                  className="remodra-input resize-none" 
                  rows={4}
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        </div>
        
        <div className="flex justify-end space-x-3 pt-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onCancel}
            className="remodra-button-outline"
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            disabled={isSubmitting}
            className="remodra-button"
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {client ? "Update Client" : "Create Client"}
          </Button>
        </div>
      </form>
    </Form>
  );
}